/**
 * GeoDirectory Map Filters
 *
 * Builds Bootstrap 5.3 map filter UI (CPT dropdown, search input, category tree) from JSON data.
 * Replaces server-side HTML rendering with modern client-side approach.
 */

export class MapFilters {
	/**
	 * Build all filters from embedded JSON data (no API call).
	 * This is the preferred method on initial page load.
	 *
	 * @param {string} mapCanvas - Map canvas ID
	 * @param {object} options - Additional options
	 * @param {boolean} options.childCollapse - Whether children are collapsed by default
	 * @returns {object|null} Object with filter elements {postTypeDropdown, searchInput, categoryTree}
	 */
	static buildFromEmbeddedData(mapCanvas, options = {}) {

		// Try to find embedded JSON data
		const scriptTag = document.getElementById(`${mapCanvas}_filters_data`);
		if (!scriptTag) {
			return null;
		}

		let filterData;
		try {
			filterData = JSON.parse(scriptTag.textContent);
		} catch (error) {
			return null;
		}

		const result = {
			postTypeDropdown: null,
			searchInput: null,
			categoryTree: null
		};

		// Build post type dropdown if enabled
		if (filterData.post_type_filter_enabled && filterData.post_types && Object.keys(filterData.post_types).length > 1) {
			result.postTypeDropdown = this.buildPostTypeDropdown(
				filterData.post_types,
				filterData.current_post_type,
				mapCanvas
			);
		}

		// Build search input if enabled
		if (filterData.search_enabled) {
			result.searchInput = this.buildSearchInput(mapCanvas);
		}

		// Build category tree if we have terms
		if (filterData.terms_filter && filterData.terms_filter.length > 0) {
			result.categoryTree = this.buildFilterTree(
				filterData.terms_filter,
				mapCanvas,
				options.childCollapse || false
			);
		}

		return result;
	}

	/**
	 * Fetch and build filters from REST API.
	 * Used when CPT dropdown changes or embedded data is not available.
	 *
	 * @param {object} options - Configuration options
	 * @param {string} options.mapCanvas - Map canvas ID
	 * @param {string} options.postType - Post type
	 * @param {string} options.restUrl - REST API base URL
	 * @param {string} options.restNonce - REST API nonce
	 * @param {boolean} options.childCollapse - Whether children are collapsed by default
	 * @param {string} options.terms - Include/exclude term IDs
	 * @param {string} options.tickTerms - Tick/untick term IDs
	 * @param {boolean} options.searchFilter - Whether search filter is enabled
	 * @param {boolean} options.postTypeFilter - Whether post type filter is enabled
	 * @returns {Promise<object|null>} Object with filter elements
	 */
	static async fetchAndBuild(options) {
		const {
			mapCanvas,
			postType,
			restUrl = '/wp-json/geodir/v3',
			restNonce = '',
			childCollapse = false,
			terms = '',
			tickTerms = '',
			searchFilter = false,
			postTypeFilter = false
		} = options;


		// Build API URL
		const params = new URLSearchParams({
			post_type: postType,
			output: 'terms',
			map_canvas: mapCanvas,
			child_collapse: childCollapse ? '1' : '0',
			search_filter: searchFilter ? '1' : '0',
			post_type_filter: postTypeFilter ? '1' : '0'
		});

		if (terms) {
			params.append('terms', terms);
		}
		if (tickTerms) {
			params.append('tick_terms', tickTerms);
		}

		const url = `${restUrl}/markers?${params.toString()}`;

		try {
			const response = await fetch(url, {
				headers: {
					'X-WP-Nonce': restNonce
				}
			});

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			const data = await response.json();

			const result = {
				postTypeDropdown: null,
				searchInput: null,
				categoryTree: null
			};

			// Build post type dropdown if enabled
			if (data.post_type_filter_enabled && data.post_types && Object.keys(data.post_types).length > 1) {
				result.postTypeDropdown = this.buildPostTypeDropdown(
					data.post_types,
					data.current_post_type,
					mapCanvas
				);
			}

			// Build search input if enabled
			if (data.search_enabled) {
				result.searchInput = this.buildSearchInput(mapCanvas);
			}

			// Build category tree if we have terms
			if (data.terms_filter && data.terms_filter.length > 0) {
				result.categoryTree = this.buildFilterTree(data.terms_filter, mapCanvas, childCollapse);
			}

			return result;

		} catch (error) {
			return null;
		}
	}

	/**
	 * Build post type dropdown (CPT filter).
	 *
	 * @param {object} postTypes - Post types object {slug: label}
	 * @param {string} currentPostType - Currently selected post type
	 * @param {string} mapCanvas - Map canvas ID
	 * @returns {HTMLElement}
	 */
	static buildPostTypeDropdown(postTypes, currentPostType, mapCanvas) {
		const select = document.createElement('select');
		select.id = `${mapCanvas}_posttype`;
		select.className = 'form-select form-select-sm mb-1 bg-dark-subtle';
		select.setAttribute('aria-label', 'Post Type');

		// Note: onchange handler is set up separately via setupPostTypeChangeHandler()
		// to properly handle API calls and filter updates

		// Build options
		for (const [slug, label] of Object.entries(postTypes)) {
			const option = document.createElement('option');
			option.value = slug;
			option.textContent = label;
			if (slug === currentPostType) {
				option.selected = true;
			}
			select.appendChild(option);
		}

		return select;
	}

	/**
	 * Build search input filter.
	 *
	 * @param {string} mapCanvas - Map canvas ID
	 * @returns {HTMLElement}
	 */
	static buildSearchInput(mapCanvas) {
		const input = document.createElement('input');
		input.type = 'search';
		input.id = `${mapCanvas}_search_string`;
		input.name = 'search';
		input.className = 'form-control form-control-sm bg-light-subtle';
		input.placeholder = 'Search by name';
		input.autocomplete = 'off';
		input.setAttribute('aria-label', 'Search by name');
		input.style.boxSizing = 'border-box';

		// Note: Event handler is set up separately via setupSearchInputHandler()
		// to properly handle debounced search and marker updates

		return input;
	}

	/**
	 * Build category filter tree HTML from JSON data.
	 *
	 * @param {Array} categories - Hierarchical category tree
	 * @param {string} mapCanvas - Map canvas ID
	 * @param {boolean} childCollapse - Whether children are collapsed by default
	 * @param {number} level - Current nesting level (for recursion)
	 * @returns {HTMLElement}
	 */
	static buildFilterTree(categories, mapCanvas, childCollapse = false, level = 0) {
		const ul = document.createElement('ul');

		// Root level classes
		if (level === 0) {
			ul.className = 'treeview main_list geodir-map-terms list-unstyled p-0 m-0';
		} else {
			ul.className = 'treeview sub_list list-unstyled p-0 m-0 ms-2';
			if (childCollapse) {
				ul.style.display = 'none';
			}
		}

		// Add left margin for nesting (15px per level, matching PHP version)
		ul.style.marginLeft = `${level * 15}px`;

		categories.forEach(category => {
			const li = this.buildCategoryItem(category, mapCanvas, childCollapse, level);
			ul.appendChild(li);
		});

		return ul;
	}

	/**
	 * Build a single category item with checkbox and children.
	 *
	 * @param {object} category - Category data
	 * @param {string} mapCanvas - Map canvas ID
	 * @param {boolean} childCollapse - Whether children are collapsed by default
	 * @param {number} level - Current nesting level
	 * @returns {HTMLElement}
	 */
	static buildCategoryItem(category, mapCanvas, childCollapse, level) {
		const li = document.createElement('li');
		li.className = 'list-unstyled p-0 m-0';

		// Create checkbox wrapper (Bootstrap form-check)
		const checkboxDiv = document.createElement('div');
		checkboxDiv.className = 'form-check';

		// Create checkbox input
		const checkbox = document.createElement('input');
		checkbox.type = 'checkbox';
		checkbox.className = 'form-check-input group_selector';
		checkbox.id = `${mapCanvas}_tick_cat_${category.id}`;
		checkbox.name = `${mapCanvas}_cat[]`;
		checkbox.value = category.id;
		checkbox.checked = category.checked;
		checkbox.onclick = function() {
			// Call the global map filter function (compatibility with existing system)
			if (typeof window.build_map_ajax_search_param === 'function') {
				window.build_map_ajax_search_param(mapCanvas, false, this);
			} else {
			}
		};

		// Create label with icon and text
		const label = document.createElement('label');
		label.className = 'form-check-label text-light mb-0';
		label.htmlFor = checkbox.id;

		// Add category icon
		const img = document.createElement('img');
		img.src = category.icon;
		img.alt = category.icon_alt || category.name;
		img.title = category.name;
		img.className = 'w-auto me-1 ms-n1 rounded-circle';
		img.style.height = '22px';
		img.loading = 'lazy';

		label.appendChild(img);
		label.appendChild(document.createTextNode(category.name));

		// Assemble checkbox
		checkboxDiv.appendChild(checkbox);
		checkboxDiv.appendChild(label);
		li.appendChild(checkboxDiv);

		// Recursively add children if they exist
		if (category.children && category.children.length > 0) {
			const childTree = this.buildFilterTree(category.children, mapCanvas, childCollapse, level + 1);
			li.appendChild(childTree);
		}

		return li;
	}

	/**
	 * Inject all filters into the map container.
	 *
	 * @param {object} filters - Object with filter elements {postTypeDropdown, searchInput, categoryTree}
	 * @param {string} mapCanvas - Map canvas ID
	 */
	static injectIntoMap(filters, mapCanvas) {
		const container = document.getElementById(`${mapCanvas}_cat`);

		if (!container) {
			return;
		}

		// Clear loading spinner or existing content
		container.innerHTML = '';

		// Insert filters in order: CPT dropdown -> Search input -> Category tree
		if (filters.postTypeDropdown) {
			container.appendChild(filters.postTypeDropdown);
		}

		if (filters.searchInput) {
			container.appendChild(filters.searchInput);
		}

		if (filters.categoryTree) {
			// Wrap category tree in geodir_toggle div if it doesn't exist
			let geodirToggle = container.querySelector('.geodir_toggle');
			if (!geodirToggle) {
				geodirToggle = document.createElement('div');
				geodirToggle.className = 'geodir_toggle my-1';
				container.appendChild(geodirToggle);
			}

			// Add hidden inputs for category filter
			const childCollapseInput = document.createElement('input');
			childCollapseInput.type = 'hidden';
			childCollapseInput.id = `${mapCanvas}_child_collapse`;
			childCollapseInput.value = filters.categoryTree.dataset.childCollapse || '0';

			const catEnabledInput = document.createElement('input');
			catEnabledInput.type = 'hidden';
			catEnabledInput.id = `${mapCanvas}_cat_enabled`;
			catEnabledInput.value = '1';

			container.insertBefore(childCollapseInput, geodirToggle);
			container.insertBefore(catEnabledInput, geodirToggle);

			geodirToggle.appendChild(filters.categoryTree);

			// Initialize collapse buttons if needed
			if (typeof window.geodir_show_sub_cat_collapse_button === 'function') {
				window.geodir_show_sub_cat_collapse_button(mapCanvas);
			}
		}

	}

	/**
	 * Get selected category IDs from the filter.
	 *
	 * @param {string} mapCanvas - Map canvas ID
	 * @returns {Array<number>} Array of selected category IDs
	 */
	static getSelectedCategories(mapCanvas) {
		const checkboxes = document.querySelectorAll(`input[name="${mapCanvas}_cat[]"]:checked`);
		return Array.from(checkboxes).map(cb => parseInt(cb.value, 10));
	}

	/**
	 * Update markers when category selection changes.
	 * Preserves search term if present.
	 *
	 * @param {string} mapCanvas - Map canvas ID
	 * @param {object} markerManager - MarkerManager instance
	 */
	static setupCategoryChangeHandler(mapCanvas, markerManager) {
		const container = document.getElementById(`${mapCanvas}_cat`);
		if (!container) {
			return;
		}

		container.addEventListener('change', (event) => {
			if (event.target.matches('input[type="checkbox"]')) {
				const selectedTerms = this.getSelectedCategories(mapCanvas);

				// Get current search value to preserve it
				const searchInput = document.getElementById(`${mapCanvas}_search_string`);
				const searchValue = searchInput ? searchInput.value.trim() : '';

				// Build additional filters object
				const additionalFilters = {};
				if (searchValue) {
					additionalFilters.search = searchValue;
				}

				// Reload markers with new category filters + preserved search
				if (markerManager && typeof markerManager.filterByTerms === 'function') {
					markerManager.filterByTerms(selectedTerms, null, additionalFilters);
				}
			}
		});

	}

	/**
	 * Setup post type (CPT) change handler.
	 *
	 * When CPT changes:
	 * 1. Fetch new categories for that post type from API
	 * 2. Rebuild category filter UI
	 * 3. Reload markers for new post type
	 *
	 * @param {string} mapCanvas - Map canvas ID
	 * @param {object} markerManager - MarkerManager instance
	 * @param {object} options - Filter options
	 */
	static setupPostTypeChangeHandler(mapCanvas, markerManager, options = {}) {
		const select = document.getElementById(`${mapCanvas}_posttype`);
		if (!select) {
			return;
		}

		const {
			restUrl = '/wp-json/geodir/v3',
			restNonce = '',
			childCollapse = false,
			terms = '',
			tickTerms = '',
			tags = '',
			searchFilter = false,
			postTypeFilter = true
		} = options;

		select.addEventListener('change', async (event) => {
			const newPostType = event.target.value;

			// Show loading state
			const container = document.getElementById(`${mapCanvas}_cat`);
			if (container) {
				const geodirToggle = container.querySelector('.geodir_toggle');
				if (geodirToggle) {
					geodirToggle.innerHTML = '<div class="py-2 px-1 small"><div role="status" class="spinner-border text-white d-inline-block align-middle"></div><div class="ml-2 ms-2 d-inline-block align-middle">Loading categories...</div></div>';
				}
			}

			try {
				// Fetch new filters from API
				const newFilters = await this.fetchAndBuild({
					mapCanvas,
					postType: newPostType,
					restUrl,
					restNonce,
					childCollapse,
					terms,
					tickTerms,
					searchFilter,
					postTypeFilter
				});

				if (newFilters) {
					// Re-inject filters (skip CPT dropdown since it's already changed)
					const filtersToInject = {
						postTypeDropdown: null, // Don't replace the dropdown
						searchInput: newFilters.searchInput,
						categoryTree: newFilters.categoryTree
					};

					// Clear and rebuild category section only
					if (container) {
						const geodirToggle = container.querySelector('.geodir_toggle');
						if (geodirToggle && newFilters.categoryTree) {
							geodirToggle.innerHTML = '';
							geodirToggle.appendChild(newFilters.categoryTree);

							// Re-setup category change handler
							this.setupCategoryChangeHandler(mapCanvas, markerManager);

							// Initialize collapse buttons
							if (typeof window.geodir_show_sub_cat_collapse_button === 'function') {
								window.geodir_show_sub_cat_collapse_button(mapCanvas);
							}
						}
					}

					// Clear search input when CPT changes (different content type = fresh start)
					const searchInput = document.getElementById(`${mapCanvas}_search_string`);
					if (searchInput) {
						searchInput.value = '';
					}

					// Reload markers for new post type
					const selectedCategories = this.getSelectedCategories(mapCanvas);

					if (markerManager && typeof markerManager.loadDirectoryMarkers === 'function') {
						await markerManager.loadDirectoryMarkers(newPostType, {
							term: selectedCategories,
							tag: tags ? tags.split(',') : []
						});
					}
				}
			} catch (error) {
			}
		});

	}

	/**
	 * Setup search input handler with debounced typing.
	 *
	 * Triggers marker search after user stops typing for 400ms.
	 * Preserves current CPT and category selections.
	 *
	 * @param {string} mapCanvas - Map canvas ID
	 * @param {object} markerManager - MarkerManager instance
	 * @param {string} postType - Current post type
	 * @param {object} options - Filter options
	 */
	static setupSearchInputHandler(mapCanvas, markerManager, postType, options = {}) {
		const input = document.getElementById(`${mapCanvas}_search_string`);
		if (!input) {
			return;
		}

		const { tags = '' } = options;
		let searchTimeout = null;

		// Debounced search on input event
		input.addEventListener('input', (event) => {
			const searchValue = event.target.value.trim();

			// Clear existing timeout
			if (searchTimeout) {
				clearTimeout(searchTimeout);
			}

			// Set new timeout for debounced search
			searchTimeout = setTimeout(async () => {

				// Get current post type (might have changed via dropdown)
				const postTypeSelect = document.getElementById(`${mapCanvas}_posttype`);
				const currentPostType = postTypeSelect ? postTypeSelect.value : postType;

				// Get selected categories
				const selectedCategories = this.getSelectedCategories(mapCanvas);

				// Build filters object
				const filters = {
					term: selectedCategories,
					tag: tags ? tags.split(',') : []
				};

				// Add search parameter if not empty
				if (searchValue) {
					filters.search = searchValue;
				}


				// Reload markers
				if (markerManager && typeof markerManager.loadDirectoryMarkers === 'function') {
					try {
						await markerManager.loadDirectoryMarkers(currentPostType, filters);
					} catch (error) {
					}
				}
			}, 800); // 800ms debounce delay
		});

	}

	/**
	 * Setup filter minimize/maximize toggle with Bootstrap collapse.
	 *
	 * Clicking the chevron icon toggles between:
	 * - Expanded: shows filters, chevron-down icon
	 * - Minimized: hides filters, sliders icon
	 *
	 * @param {string} mapCanvas - Map canvas ID
	 */
	static setupFilterToggle(mapCanvas) {
		const container = document.getElementById(`${mapCanvas}_cat`);
		if (!container) {
			return;
		}

		// Find the trigger element
		const trigger = container.parentElement.querySelector('.gd-trigger');
		if (!trigger) {
			return;
		}

		const chevronIcon = trigger.querySelector('.fa-chevron-down');
		const slidersIcon = trigger.querySelector('.fa-sliders-h');

		// Add Bootstrap collapse classes and data attributes
		// Keep the original ID for compatibility with other functions
		const collapseId = `${mapCanvas}_cat`;
		container.classList.add('collapse', 'show');

		trigger.setAttribute('data-bs-toggle', 'collapse');
		trigger.setAttribute('data-bs-target', `#${collapseId}`);
		trigger.setAttribute('aria-expanded', 'true');
		trigger.setAttribute('aria-controls', collapseId);

		// Listen to Bootstrap collapse events to toggle icons
		container.addEventListener('hide.bs.collapse', () => {
			if (chevronIcon) chevronIcon.classList.add('d-none');
			if (slidersIcon) slidersIcon.classList.remove('d-none');
			trigger.classList.remove('gd-triggeroff');
			trigger.classList.add('gd-triggered');
			trigger.setAttribute('aria-expanded', 'false');
		});

		container.addEventListener('show.bs.collapse', () => {
			if (chevronIcon) chevronIcon.classList.remove('d-none');
			if (slidersIcon) slidersIcon.classList.add('d-none');
			trigger.classList.remove('gd-triggered');
			trigger.classList.add('gd-triggeroff');
			trigger.setAttribute('aria-expanded', 'true');
		});

	}
}

